'use client';

import React, { useCallback, useMemo } from 'react';

import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { useGetSystemSitesPaginatedInfiniteQuery } from 'src/api/redux';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import { useResolveSystemId } from 'src/components/Profiles/shared/widgets/SystemVolume/useResolveSystemId';
import { buildVolumeConfig, sumRowVolume } from './volumeConfigUtils';

const PAGE_SIZE = 25;

const CompactSystemSites: React.FC = () => {
  const { entityDetails } = useProfile();
  const { openDrawer } = useDrawer();
  const { dispatch } = useSite();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const systemId = useResolveSystemId(
    entityDetails,
    widget?.network_rollup === true
  );
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const volumeType = widget?.volume_type ?? 'claims';

  const volumeConfig = useMemo(
    () => buildVolumeConfig(widget, widgetConfig?.minYear, widgetConfig?.maxYear),
    [widget, widgetConfig?.minYear, widgetConfig?.maxYear]
  );

  const hasVolume = Object.keys(volumeConfig).length > 0;

  const handleSiteClick = useCallback(
    (row: Record<string, any>) => {
      dispatch({
        type: 'SET_CURRENT_PROVIDER',
        payload: { id: row.provider_id, type: row.type || 'clinic' },
      });
      openDrawer('profile');
    },
    [dispatch, openDrawer]
  );

  const {
    data: paginatedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetSystemSitesPaginatedInfiniteQuery(
    {
      systemId,
      pageSize: PAGE_SIZE,
      sort: hasVolume ? 'volume' : 'name',
      order: hasVolume ? 'desc' : 'asc',
      sort_field: hasVolume ? volumeType : undefined,
      q: '',
      filterSets: widgetFilterSetId ? [widgetFilterSetId] : undefined,
      data: hasVolume ? volumeConfig : undefined,
    },
    { skip: !systemId }
  );

  const rows = useMemo(
    () => paginatedData?.pages?.flatMap((page: any) => page.results) ?? [],
    [paginatedData]
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || !!isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-system-sites"
    >
      <WidgetHeader fallbackTitle="Sites" />

      <div className="flex-1 min-h-0 max-h-80 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl">
        {isLoading ? (
          <WidgetLoading data-testid="compact-system-sites-loading" />
        ) : rows.length === 0 ? (
          <WidgetEmpty data-testid="compact-system-sites-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="System Sites"
            data-testid="compact-system-sites-list"
          >
            {hasVolume && (
              <div
                className="flex items-center justify-end pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10"
                role="row"
                aria-label="Column headers"
              >
                <span
                  className="text-xs font-semibold text-tertiary min-w-16 text-end"
                  role="columnheader"
                >
                  {volumeType === 'patients' ? 'Patients' : 'Claims'}
                </span>
              </div>
            )}
            {rows.map((row: Record<string, any>) => {
              const total = sumRowVolume(row, volumeType);
              return (
                <div
                  key={row.provider_id || row.id}
                  className="flex flex-col py-1.5 border-b border-neutral-100 last:border-b-0"
                  role="listitem"
                  data-testid={`compact-system-sites-row-${row.provider_id || row.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleSiteClick(row)}
                      className="text-sm font-bold text-link truncate hover:underline text-left"
                      data-testid={`compact-system-sites-link-${row.provider_id}`}
                    >
                      {row.name || 'N/A'}
                    </button>
                    {total > 0 && (
                      <span className="text-sm font-semibold text-teal-900 tabular-nums flex-shrink-0">
                        {handleNumericValue(total)}
                      </span>
                    )}
                  </div>
                  {(row.city || row.state) && (
                    <span className="text-xs text-tertiary truncate">
                      {[row.city, row.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div
              ref={fetchTriggerRef}
              className="h-1"
              aria-hidden="true"
              data-testid="compact-system-sites-scroll-trigger"
            />
            {isFetchingNextPage && (
              <WidgetLoading />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactSystemSites;
